import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import { normalizeTimeString } from '@gat-ship/utils'
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { getCurrentTimeZoneId } from './date-utils'

/** Props for {@link useTimeInput}. */
export interface UseTimeInputProps {
  /** The bound value — a UTC instant string, or `undefined` when empty. */
  modelValue: MaybeRefOrGetter<ISODateTimeString | undefined>
  /** Timezone the time-of-day is displayed and interpreted in. */
  timeZone?: MaybeRefOrGetter<string | undefined>
}

/**
 * Backs a plain text time input. Formats the time-of-day of the bound
 * instant for display and, on blur, expands whatever the user typed
 * (`9`, `930`, `9:3`) into a canonical `HH:mm`.
 *
 * The time counterpart of `useDateInput` — same focus/input/blur shape,
 * parsing delegated to the pure `normalizeTimeString`. On blur it
 * replaces only the time-of-day, keeping the model's date; a fresh
 * value starts from the current date. Blank input clears the value;
 * unparseable input is dropped.
 */
export function useTimeInput(
  props: UseTimeInputProps,
  updateModel: (value: ISODateTimeString | undefined) => void
) {
  const timeZone = computed(
    () => toValue(props.timeZone) ?? getCurrentTimeZoneId()
  )

  /** The bound instant as a zoned date-time, or `undefined` when empty. */
  const zonedModel = computed(() => {
    const value = toValue(props.modelValue)
    return value
      ? Temporal.Instant.from(value).toZonedDateTimeISO(timeZone.value)
      : undefined
  })

  /** The model's time-of-day formatted as `HH:mm`, or `''` when empty. */
  const formattedTime = computed(() => {
    const zoned = zonedModel.value
    if (!zoned) return ''
    return zoned.toLocaleString(
      Intl.DateTimeFormat().resolvedOptions().locale,
      {
        timeStyle: 'short',
        hour12: false
      }
    )
  })

  // While the field has focus the user's keystrokes are shown verbatim
  // (`draft`); once it blurs, the display snaps back to `formattedTime`.
  const isEditing = ref(false)
  const draft = ref('')

  /** What the bound text input should display. */
  const timeInputValue = computed(() =>
    isEditing.value ? draft.value : formattedTime.value
  )

  /** Call when the input gains focus. */
  function onFocus() {
    draft.value = formattedTime.value
    isEditing.value = true
  }

  /** Call with the input's current text whenever it changes. */
  function onInput(value: string) {
    draft.value = value
  }

  /** Call when the input loses focus — commits the typed value. */
  function onBlur() {
    isEditing.value = false
    const typed = draft.value

    // Unchanged from the canonical display — nothing to commit.
    if (typed === formattedTime.value) return

    // Cleared — drop the value. The caller decides what an empty time
    // means; `FormDateTimeField` tracks the date and time halves
    // separately, so blanking the time makes the field incomplete again.
    if (!typed.trim()) {
      updateModel(undefined)
      return
    }

    const normalized = normalizeTimeString(typed)
    // Unparseable — leave the model untouched. Clearing `isEditing`
    // already snapped the display back to the canonical value.
    if (!normalized) return

    const [hourPart, minutePart] = normalized.split(':')
    const hour = Number(hourPart)
    const minute = Number(minutePart)

    // Replace only the time-of-day, keeping the model's date. A fresh
    // value starts from the current date.
    const base =
      zonedModel.value ?? Temporal.Now.zonedDateTimeISO(timeZone.value)
    const next = base.with({ hour, minute })

    updateModel(next.toInstant().toString() as ISODateTimeString)
  }

  return { timeInputValue, onFocus, onInput, onBlur }
}
